import { ContractStatusBadge } from '@/components/ContractStatusBadge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { VehicleInfo } from '@/features/contractSearch/types';
import { openCheckDetailsEmail } from '@/lib/emailUtils';
import { formatCurrency } from '@/lib/utils';

import React, { useEffect } from 'react';
import { toast } from 'sonner';
import { useCancellationDetails } from '../hooks/useCancellationDetails';
import { ContractDetailsSkeleton } from './ContractDetailsSkeleton';

interface ContractDetailsModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Handler for modal open/close state changes */
  onOpenChange: (open: boolean) => void;
  /** The contract number to fetch details for and display */
  contractNumber: string;
  /** Customer name for display */
  customerName: string;
  /** Vehicle information for display */
  vehicleInfo: VehicleInfo;
  /** Contract status */
  status: string;
}

/**
 * Modal component for displaying contract cancellation details.
 *
 * Fetches and displays detailed cancellation information including refund amounts,
 * fees, dates, and other relevant cancellation data. Uses TanStack Query for
 * data fetching with 5-minute caching.
 */
export const ContractDetailsModal = React.memo(function ContractDetailsModal({
  open,
  onOpenChange,
  contractNumber,
  customerName,
  vehicleInfo,
  status,
}: ContractDetailsModalProps) {
  const { data, isLoading, isError, error } = useCancellationDetails(
    contractNumber,
    open
  );

  /**
   * Handles opening the check details email in a new tab/window
   */
  const handleEmailCheckDetails = () => {
    openCheckDetailsEmail(contractNumber, vehicleInfo.vin, customerName);
  };

  useEffect(() => {
    if (isError && error) {
      toast.error(error.message || 'Failed to load contract details');
    }
  }, [isError, error]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-2xl max-h-[90vh] overflow-y-auto focus:outline-none w-[95vw] sm:w-full'>
        <DialogHeader>
          <DialogTitle className='text-lg font-semibold'>
            Contract Details
          </DialogTitle>
          <DialogDescription />
        </DialogHeader>

        <div className='space-y-6'>
          {/* Contract Header */}
          <Card className='bg-gradient-to-r from-background to-accent/10 border-accent/30'>
            <CardContent className='space-y-4'>
              <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3'>
                <div>
                  <h3
                    className='text-lg font-semibold text-foreground'
                    id='contract-number'
                  >
                    {contractNumber}
                  </h3>
                  <p
                    className='text-sm text-muted-foreground'
                    id='customer-name'
                  >
                    {customerName}
                  </p>
                </div>
                <ContractStatusBadge status={status} />
              </div>

              <div
                className='text-sm text-muted-foreground bg-muted/30 rounded-lg p-3 border border-border/50'
                role='group'
                aria-label='Vehicle information'
              >
                <div className='mb-2'>
                  <span className='font-semibold text-foreground text-base'>
                    Vehicle Details
                  </span>
                </div>
                <p className='font-medium text-foreground'>
                  {vehicleInfo.year} {vehicleInfo.make} {vehicleInfo.model}
                </p>
                <p className='text-xs text-muted-foreground'>
                  VIN: {vehicleInfo.vin}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Content Area */}
          {isLoading && (
            <Card
              className='bg-gradient-to-br from-background to-accent/10 border-accent/30'
              role='status'
              aria-label='Loading cancellation details'
              aria-live='polite'
            >
              <CardContent>
                <div className='mb-4'>
                  <h4 className='text-base font-semibold text-foreground border-b border-border/50 pb-2'>
                    Loading Cancellation Details...
                  </h4>
                </div>
                <ContractDetailsSkeleton />
              </CardContent>
            </Card>
          )}

          {isError && (
            <Card
              className='bg-destructive/5 border-destructive/20'
              role='alert'
              aria-live='assertive'
            >
              <CardContent className='text-center py-8'>
                <div className='flex items-center justify-center gap-2 mb-2'>
                  <div className='h-2 w-2 rounded-full bg-destructive'></div>
                  <span className='font-semibold text-destructive'>
                    Error Loading Details
                  </span>
                </div>
                <p className='text-sm text-destructive/80'>
                  Failed to load cancellation details. Please try again.
                </p>
              </CardContent>
            </Card>
          )}

          {data && (
            <Card
              className='animate-in fade-in-0 duration-300 bg-gradient-to-br from-background to-accent/10 border-accent/30'
              role='region'
              aria-labelledby='cancellation-details-heading'
            >
              <CardContent className='space-y-6'>
                <div className='mb-4'>
                  <h4
                    id='cancellation-details-heading'
                    className='text-lg font-semibold text-foreground border-b border-border/50 pb-2'
                  >
                    Cancellation Details
                  </h4>
                </div>

                <div className='grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-4 text-sm'>
                  <div
                    className='space-y-3'
                    role='group'
                    aria-label='Core cancellation details'
                  >
                    <div className='flex justify-between'>
                      <span className='text-muted-foreground'>
                        Cancel Status:
                      </span>
                      <span
                        className='font-medium'
                        aria-label={`Cancel status: ${data.cancelStatus}`}
                      >
                        {data.cancelStatus}
                      </span>
                    </div>

                    <div className='flex justify-between'>
                      <span className='text-muted-foreground'>
                        Cancel Date:
                      </span>
                      <span
                        className='font-medium'
                        aria-label={`Cancel date: ${data.cancelDate}`}
                      >
                        {data.cancelDate}
                      </span>
                    </div>

                    <div className='flex justify-between'>
                      <span className='text-muted-foreground'>
                        Cancel Mileage:
                      </span>
                      <span
                        className='font-medium'
                        aria-label={`Cancel mileage: ${data.cancelMileage.toLocaleString()} miles`}
                      >
                        {data.cancelMileage.toLocaleString()}
                      </span>
                    </div>

                    <div className='flex justify-between'>
                      <span className='text-muted-foreground'>
                        Cancel Reason:
                      </span>
                      <span
                        className='font-medium'
                        aria-label={`Cancel reason: ${data.cancelReason}`}
                      >
                        {data.cancelReason}
                      </span>
                    </div>

                    <div className='flex justify-between'>
                      <span className='text-muted-foreground'>Days Used:</span>
                      <span
                        className='font-medium'
                        aria-label={`Days used: ${data.daysUsed}`}
                      >
                        {data.daysUsed}
                      </span>
                    </div>

                    <div className='flex justify-between'>
                      <span className='text-muted-foreground'>Miles Used:</span>
                      <span
                        className='font-medium'
                        aria-label={`Miles used: ${data.milesUsed}`}
                      >
                        {data.milesUsed}
                      </span>
                    </div>

                    <div className='flex justify-between'>
                      <span className='text-muted-foreground'>
                        Contract Used:
                      </span>
                      <span
                        className='font-medium'
                        aria-label={`Contract used: ${data.contractUsed}`}
                      >
                        {data.contractUsed}
                      </span>
                    </div>
                  </div>

                  <div
                    className='space-y-3'
                    role='group'
                    aria-label='Financial details'
                  >
                    <div className='flex justify-between'>
                      <span className='text-muted-foreground'>
                        Customer Refund Amount:
                      </span>
                      <span
                        className='font-medium'
                        aria-label={`Customer Refund amount: ${formatCurrency(data.customerRefundAmount)}`}
                      >
                        {formatCurrency(data.customerRefundAmount)}
                      </span>
                    </div>

                    <div className='flex justify-between'>
                      <span className='text-muted-foreground'>
                        Store Refund Amount:
                      </span>
                      <span
                        className='font-medium'
                        aria-label={`Store Refund amount: ${formatCurrency(data.storeRefund)}`}
                      >
                        {formatCurrency(data.storeRefund)}
                      </span>
                    </div>

                    <div className='flex justify-between'>
                      <span className='text-muted-foreground'>Cancel Fee:</span>
                      <span
                        className='font-medium'
                        aria-label={`Cancel fee: ${formatCurrency(data.cancelFee)}`}
                      >
                        {formatCurrency(data.cancelFee)}
                      </span>
                    </div>

                    <div className='flex justify-between'>
                      <span className='text-muted-foreground'>
                        Claims Deducted:
                      </span>
                      <span
                        className='font-medium'
                        aria-label={`Claims deducted: ${data.claimsDeducted ? 'Yes' : 'No'}`}
                      >
                        {data.claimsDeducted ? 'Yes' : 'No'}
                      </span>
                    </div>

                    {data.claimsAmount > 0 && (
                      <div className='flex justify-between'>
                        <span className='text-muted-foreground'>
                          Claims Amount:
                        </span>
                        <span
                          className='font-medium'
                          aria-label={`Claims amount: ${formatCurrency(data.claimsAmount)}`}
                        >
                          {formatCurrency(data.claimsAmount)}
                        </span>
                      </div>
                    )}

                    <div className='flex justify-between'>
                      <span className='text-muted-foreground'>Payee Name:</span>
                      <span
                        className='font-medium'
                        aria-label={`Payee name: ${data.payeeName}`}
                      >
                        {data.payeeName}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Check Information Notice */}
          <Card className='bg-gradient-to-br from-background to-accent/10 border-accent/30'>
            <CardContent>
              <div className='text-sm text-foreground'>
                <div className='font-semibold mb-3 text-foreground text-base border-b border-border/50 pb-2'>
                  Check Information
                </div>
                <div className='text-foreground'>
                  Checks are issued by Total Care Auto. To get check details
                  please email{' '}
                  <button
                    type='button'
                    onClick={handleEmailCheckDetails}
                    className='font-semibold text-accent hover:text-accent/80 underline cursor-pointer bg-transparent border-none p-0 m-0'
                  >
                    accountdept@landcar.com
                  </button>
                  .
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
});
